if (!customElements.get('rewards-context')) {
  customElements.define(
    'rewards-context',
    class extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.refresh();
      }

      async refresh() {
        const params = new URLSearchParams({
          include_unused_rewards: 'true',
          include_full_program: 'false',
        });

        const request = await fetch(`/apps/rewards/api/balance?${params}`);

        if (!request.ok) {
          throw new Error('Failed to fetch rewards data');
        }

        const response = await request.json();

        this.data = response;
      }

      /**
       * @param {number} points - The number of points to redeem.
       * @param {boolean} [refresh] - Refresh the balance after redeeming. Defaults to true.
       * @returns {Promise<any>} The redeem API response.
       */
      async redeem(points, refresh = true) {
        const request = await fetch('/apps/rewards/api/redeem', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ pts: points }),
        });

        const response = await request.json();

        if (!request.ok) {
          if (response.error === 'insufficient_points') {
            throw new Error('Insufficient points');
          }

          throw new Error('Failed to redeem rewards');
        }

        if (refresh) {
          await this.refresh();
        }

        return response;
      }

      /**
       * @param {typeof window.rewardsData} data Rewards payload.
       */
      emit(data) {
        window.dispatchEvent(new CustomEvent('rewards-data-updated', { detail: data }));
      }

      /**
       * @param {typeof window.rewardsData} data Rewards payload.
       */
      set data(data) {
        window.rewardsData = data;
        this.emit(data);
      }
    },
  );
}

if (!customElements.get('rewards-panel')) {
  customElements.define(
    'rewards-panel',
    class extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          unused: {
            value: this.querySelectorAll('[data-rewards-unused-value]'),
            list: this.querySelector('[data-rewards-unused-list]'),
            total: this.querySelector('[data-rewards-unused-total]'),
          },
          redeemableBalance: this.querySelectorAll('[data-rewards-redeemable-balance]'), // Available balance to turn into a discount
          appliedBalance: this.querySelectorAll('[data-rewards-applied-balance]'), // Value of reward discounts currently applied to the cart
          unlockedBalance: this.querySelectorAll('[data-rewards-unlocked-balance]'), // Overall balance, including available balance and unused rewards
          availableBalance: this.querySelectorAll('[data-rewards-available-balance]'), // Balance available to either redeem or apply as an overall monetary value
          availableSummary: this.querySelector('.rewards-panel-details-summary-value-available'),
          warning: this.querySelector('[data-rewards-max-codes-warning]'),
        };

        this.onRewardsDataUpdated = (/** @type {Event} */ event) =>
          this.render(/** @type {CustomEvent<typeof window.rewardsData>} */ (event).detail);

        this.onCartUpdated = (/** @type {Event} */ event) => {
          this.updateCartVisibility(/** @type {CustomEvent} */ (event).detail);
          this.updateMaxDiscountWarning();
          this.render(window.rewardsData);
        };
      }

      connectedCallback() {
        this.updateCartVisibility();
        this.updateMaxDiscountWarning();
        this.render(window.rewardsData);
        window.addEventListener('rewards-data-updated', this.onRewardsDataUpdated);
        window.addEventListener('cart-updated', this.onCartUpdated);
      }

      disconnectedCallback() {
        window.removeEventListener('rewards-data-updated', this.onRewardsDataUpdated);
        window.removeEventListener('cart-updated', this.onCartUpdated);
      }

      /**
       * @param {Record<string, unknown> | undefined} [cart] Cart state from cart-updated.
       * @returns {boolean} Whether the cart has no line items.
       */
      _cartIsEmpty(cart) {
        if (cart && typeof cart.item_count === 'number') {
          return cart.item_count === 0;
        }

        const signature = window.__cartStateSignature;

        if (!signature) return false;

        try {
          const state = JSON.parse(signature);

          return !state?.item_count;
        } catch {
          return false;
        }
      }

      /**
       * @param {Record<string, unknown> | undefined} [cart] Cart state from cart-updated.
       */
      updateCartVisibility(cart) {
        this.hidden = this._cartIsEmpty(cart);
      }

      updateMaxDiscountWarning() {
        this.elements.warning?.classList.toggle('visible', this._appliedDiscountCodes().size >= 5);
      }

      /**
       * @param {string} value - The value to format.
       * @param {number} multiplier - The multiplier to apply to the value.
       * @returns {string} The formatted value.
       */
      _money(value, multiplier) {
        const amount = Number.parseFloat(value) * multiplier;

        return new Intl.NumberFormat(document.documentElement.lang, {
          style: 'currency',
          currency: this.dataset.currency || window.Shopify.currency.active,
        }).format(amount);
      }

      /**
       * @param {typeof window.rewardsData} data - Rewards payload.
       * @returns {NonNullable<NonNullable<typeof window.rewardsData>['unused_rewards']>} Unused rewards not applied to the cart.
       */
      _unappliedRewards(data) {
        if (!data?.unused_rewards?.length) return [];

        const appliedDiscountCodes = this._appliedDiscountCodes();

        return data.unused_rewards.filter((reward) => !appliedDiscountCodes.has(reward.code.toLowerCase()));
      }

      /**
       * @param {typeof window.rewardsData} data - Rewards payload.
       * @returns {NonNullable<NonNullable<typeof window.rewardsData>['unused_rewards']>} Applied rewards from the rewards context.
       */
      _activeRewards(data) {
        if (!data?.unused_rewards?.length) return [];

        const rewardsByCode = new Map(data.unused_rewards.map((reward) => [reward.code.toLowerCase(), reward]));

        return Array.from(this._appliedDiscountCodes())
          .map((code) => rewardsByCode.get(code))
          .filter((reward) => reward !== undefined);
      }

      /** @returns {Set<string>} Lowercase discount codes already applied to the cart. */
      _appliedDiscountCodes() {
        const signature = window.__cartStateSignature;

        if (!signature) return new Set();

        try {
          const cart = JSON.parse(signature);
          const applications = cart.cart_level_discount_applications;

          if (!Array.isArray(applications)) return new Set();

          return new Set(
            applications
              .filter((application) => application.type === 'discount_code' && application.title)
              .map((application) => String(application.title).toLowerCase()),
          );
        } catch {
          return new Set();
        }
      }

      /**
       * @param {typeof window.rewardsData} data Rewards payload.
       */
      async render(data) {
        this.updateMaxDiscountWarning();

        const multiplier = await window.getExchangeRateMultiplier();
        const activeRewards = this._activeRewards(data);

        this.toggleAttribute('data-reward-applied', activeRewards.length > 0);

        // console.log('[rewards-panel] render', data);

        if (this.elements.appliedBalance.length) {
          const appliedTotal = String(activeRewards.reduce((acc, reward) => acc + Number.parseFloat(reward.amount), 0));

          this.elements.appliedBalance.forEach((element) => {
            element.textContent = this._money(appliedTotal, multiplier);
          });
        }

        if (this.elements.unlockedBalance.length) {
          const availableValue = data?.pts_available_value ? Number.parseFloat(data.pts_available_value) : 0;
          const unusedRewardsValue = data?.unused_rewards?.length
            ? data.unused_rewards.reduce((acc, reward) => acc + Number.parseFloat(reward.amount), 0)
            : 0;
          const unlockedTotal = String(availableValue + unusedRewardsValue);

          this.elements.unlockedBalance.forEach((element) => {
            element.textContent = this._money(unlockedTotal, multiplier);
          });
        }

        const redeemableValue = data?.pts_available_value ? Number.parseFloat(data.pts_available_value) : 0;

        if (this.elements.redeemableBalance.length) {
          this.elements.redeemableBalance.forEach((element) => {
            element.textContent = this._money(String(redeemableValue), multiplier);
          });
        }

        if (this.elements.availableBalance.length) {
          const unappliedRewardsValue = this._unappliedRewards(data).reduce(
            (acc, reward) => acc + Number.parseFloat(reward.amount),
            0,
          );
          const availableTotal = String(redeemableValue + unappliedRewardsValue);

          this.elements.availableBalance.forEach((element) => {
            element.textContent = this._money(availableTotal, multiplier);
          });
        }

        this.elements.availableSummary?.toggleAttribute('hidden', !(data?.pts_available ?? 0));

        if (!data || !data.unused_rewards) {
          return;
        }

        if (this.elements.unused.value && this.elements.unused.list && this.elements.unused.total) {
          this.elements.unused.list.innerHTML = '';

          if (!(data.unused_rewards && data.unused_rewards.length > 0)) {
            return;
          }

          const discounts = this._unappliedRewards(data).sort(
            (a, b) => Number.parseFloat(b.amount) - Number.parseFloat(a.amount),
          );

          if (discounts.length === 0) {
            this.elements.unused.value.forEach((element) => {
              element.textContent = this._money('0', multiplier);
            });
            this.elements.unused.total.textContent = this._money('0', multiplier);

            return;
          }

          const total = String(discounts.reduce((acc, reward) => acc + Number.parseFloat(reward.amount), 0));

          this.elements.unused.value.forEach((element) => {
            element.textContent = this._money(total, multiplier);
          });

          discounts.forEach((discount) => {
            const element = document.createElement('li');
            element.classList.add('rewards-panel-unused-reward');
            element.innerHTML = `
              <span class="text-discount-tag rewards-panel-reward-tag">${this.dataset.tagIconMarkup || ''} ${discount.code}</span>
              <span class="text-title-sm text-color-tertiary rewards-panel-unused-reward-value">${this._money(discount.amount, multiplier)}</span>
              <button class="text-button text-button--small text-button--no-underline" type="button" data-rewards-apply-code="${discount.code}">${this.dataset.translationApply || 'Apply'}</button>
            `;
            this.elements.unused.list?.append(element);
          });

          this.elements.unused.total.textContent = this._money(total, multiplier);
        }
      }
    },
  );
}

if (!customElements.get('rewards-redeem')) {
  customElements.define(
    'rewards-redeem',
    class extends HTMLElement {
      constructor() {
        super();

        /** @type {(HTMLElement & { redeem: (points: number, refresh?: boolean) => Promise<any> }) | null} */
        this.context = document.querySelector('rewards-context');

        this.redeemListenersBound = false;

        this.form = this.querySelector('form');
        this.submit = this.querySelector('[type="submit"]');
        this.presetContainer = this.querySelector('[data-rewards-redeem-preset-container]');

        this.onSubmit = this.handleSubmit.bind(this);
        this.onPresetClick = this.handlePresetClick.bind(this);
        this.onPointsInput = this.handlePointsInput.bind(this);
        this.onRewardsDataUpdated = () => {
          this.setInputMax();
          this.renderPreset();
        };
      }

      connectedCallback() {
        if (this.redeemListenersBound) return;
        this.redeemListenersBound = true;
        this.form?.addEventListener('submit', this.onSubmit);
        this.form?.addEventListener('input', this.onPointsInput);
        this.presetContainer?.addEventListener('click', this.onPresetClick);
        window.addEventListener('rewards-data-updated', this.onRewardsDataUpdated);
        this.renderPreset();
      }

      disconnectedCallback() {
        if (!this.redeemListenersBound) return;
        this.redeemListenersBound = false;
        this.form?.removeEventListener('submit', this.onSubmit);
        this.form?.removeEventListener('input', this.onPointsInput);
        this.presetContainer?.removeEventListener('click', this.onPresetClick);
        window.removeEventListener('rewards-data-updated', this.onRewardsDataUpdated);
      }

      /**
       * @param {Event} event - Input within the redeem form.
       */
      handlePointsInput(event) {
        const input = event.target;

        if (!(input instanceof HTMLInputElement) || input.name !== 'points') return;

        input.setCustomValidity('');

        const digits = input.value.replace(/\D/g, '');

        if (!digits) {
          input.value = '';
          return;
        }

        /** @type {HTMLElement | null} */
        const panel = this.closest('rewards-panel');

        const formatter = new Intl.NumberFormat(document.documentElement.lang, {
          style: 'currency',
          currency: panel?.dataset.currency || window.Shopify.currency.active,
        });

        const fractionDigits = formatter.resolvedOptions().maximumFractionDigits ?? 2;
        const amount = Number(digits) / 10 ** fractionDigits;

        input.value = formatter.format(amount);
      }

      /**
       * @param {string} value - The value to format.
       * @param {number} multiplier - The multiplier to apply to the value.
       * @returns {string} The formatted value.
       */
      _money(value, multiplier) {
        const amount = Number.parseFloat(value) * multiplier;
        /** @type {HTMLElement | null} */
        const panel = this.closest('rewards-panel');

        return new Intl.NumberFormat(document.documentElement.lang, {
          style: 'currency',
          currency: panel?.dataset.currency || window.Shopify.currency.active,
        }).format(amount);
      }

      async renderPreset() {
        if (!this.presetContainer) return;

        const data = window.rewardsData;

        if (!data || !data.pts_available) {
          this.presetContainer.replaceChildren();
          return;
        }

        const multiplier = await window.getExchangeRateMultiplier();

        const button = document.createElement('button');
        button.classList.add('rewards-panel-redeem-preset-button', 'text-button-sm');
        button.type = 'button';
        button.dataset.rewardsRedeemPreset = data.pts_available.toString();
        button.textContent = this._money(data.pts_available_value, multiplier);

        const customButton = document.createElement('button');
        customButton.classList.add('rewards-panel-redeem-preset-button', 'text-button-sm');
        customButton.type = 'button';
        customButton.dataset.rewardsRedeemCustom = '';
        customButton.setAttribute('aria-controls', 'points');
        customButton.setAttribute('aria-expanded', 'false');
        customButton.textContent = this.dataset.translationCustom || 'Custom';

        this.presetContainer.replaceChildren(button, customButton);
      }

      /** @returns {string[]} Discount codes already applied to the cart. */
      _appliedDiscountCodes() {
        const signature = window.__cartStateSignature;

        if (!signature) return [];

        try {
          const cart = JSON.parse(signature);
          const applications = cart.cart_level_discount_applications;

          if (!Array.isArray(applications)) return [];

          return applications
            .filter((application) => application.type === 'discount_code' && application.title)
            .map((application) => String(application.title));
        } catch {
          return [];
        }
      }

      /**
       * @param {Event} event - Click within the preset container.
       */
      async handlePresetClick(event) {
        const target = event.target;

        if (!(target instanceof Element)) return;

        const customButton = target.closest('[data-rewards-redeem-custom]');

        if (customButton instanceof HTMLElement) {
          customButton.dataset.rewardsRedeemCustom = 'true';
          customButton.setAttribute('aria-expanded', 'true');
          customButton.style.display = 'none';

          const pointsField = this.form?.elements.namedItem('points');

          if (pointsField instanceof HTMLInputElement) {
            pointsField.focus();
          }

          return;
        }

        const button = target.closest('[data-rewards-redeem-preset]');

        if (!(button instanceof HTMLElement)) return;

        const points = button.dataset.rewardsRedeemPreset;

        if (!points) return;

        await this.redeemAndApply(Number(points));
      }

      /**
       * Redeems points into a coupon and applies the resulting code to the cart.
       * @param {number} points - Points to redeem.
       * @returns {Promise<boolean>} Whether the redemption succeeded.
       */
      async redeemAndApply(points) {
        if (!points || this.dataset.loading === 'true') return false;

        this.dataset.loading = 'true';
        this.setRedeemError('');

        try {
          const response = await this.context?.redeem(points, false);
          const newCode = response?.code;

          const cartForm = document.querySelector('cart-form');

          if (newCode && cartForm) {
            const appliedCodes = this._appliedDiscountCodes();
            const discountCodes = appliedCodes.some((code) => code.toLowerCase() === newCode.toLowerCase())
              ? appliedCodes
              : [...appliedCodes, newCode];

            // Shopify only allows 5 discount codes to be applied to a cart.
            if (discountCodes.length <= 5) {
              await cartForm.updateDiscounts(discountCodes);
            }
          }

          return true;
        } catch (err) {
          this.setRedeemError(err instanceof Error ? err.message : 'Something went wrong');
          return false;
        } finally {
          this.dataset.loading = 'false';
          document.querySelector('rewards-context')?.refresh();
        }
      }

      /**
       * Converts the formatted currency value in the points input to a points amount.
       * @returns {Promise<number>} Points equivalent of the entered amount, or 0 when empty/invalid.
       */
      async _pointsFromInput() {
        const pointsField = this.form?.elements.namedItem('points');

        if (!(pointsField instanceof HTMLInputElement)) return 0;

        const digits = pointsField.value.replace(/\D/g, '');

        if (!digits) return 0;

        const ptValue = Number.parseFloat(window.rewardsData?.redemption?.pt_value ?? '0');

        if (!ptValue) return 0;

        /** @type {HTMLElement | null} */
        const panel = this.closest('rewards-panel');

        const formatter = new Intl.NumberFormat(document.documentElement.lang, {
          style: 'currency',
          currency: panel?.dataset.currency || window.Shopify.currency.active,
        });

        const fractionDigits = formatter.resolvedOptions().maximumFractionDigits ?? 2;
        const displayedAmount = Number(digits) / 10 ** fractionDigits;

        const multiplier = await window.getExchangeRateMultiplier();
        const baseAmount = displayedAmount / multiplier;

        return Math.round(baseAmount / ptValue);
      }

      /**
       * @param {string} [message] - Validation message; empty clears it.
       */
      setRedeemError(message = '') {
        const pointsField = this.form?.elements.namedItem('points');

        if (!(pointsField instanceof HTMLInputElement)) return;

        pointsField.setCustomValidity(message);

        if (message) {
          pointsField.reportValidity();
        }
      }

      /**
       * @param {Event} event - Form submit from the redeem form.
       */
      async handleSubmit(event) {
        event.preventDefault();

        if (!this.form) return;

        const points = await this._pointsFromInput();

        if (!points) {
          this.setRedeemError('Please enter an amount to redeem');
          return;
        }

        const available = window.rewardsData?.pts_available ?? 0;

        if (points > available) {
          this.setRedeemError(this.dataset.translationAmountExceeds || 'Amount exceeds your available balance');
          return;
        }

        const success = await this.redeemAndApply(points);

        if (success) {
          this.form.reset();
        }
      }

      setInputMax() {
        if (!this.form) return;

        const pointsField = this.form.elements.namedItem('points');

        if (pointsField instanceof HTMLInputElement) {
          pointsField.max = window.rewardsData.pts_available.toString();
        }
      }
    },
  );
}

if (!customElements.get('rewards-panel-unused-rewards')) {
  customElements.define(
    'rewards-panel-unused-rewards',
    class extends HTMLElement {
      constructor() {
        super();

        this.applyAllButton = this.querySelector('[data-rewards-unused-apply-all]');
        this.handleApplyClick = this.handleApplyClick.bind(this);

        this.onRewardsDataUpdated = (/** @type {Event} */ event) =>
          this.render(/** @type {CustomEvent<typeof window.rewardsData>} */ (event).detail);

        this.onCartUpdated = () => this.render(window.rewardsData);
      }

      connectedCallback() {
        this.render(window.rewardsData);
        this.addEventListener('click', this.handleApplyClick);
        window.addEventListener('rewards-data-updated', this.onRewardsDataUpdated);
        window.addEventListener('cart-updated', this.onCartUpdated);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleApplyClick);
        window.removeEventListener('rewards-data-updated', this.onRewardsDataUpdated);
        window.removeEventListener('cart-updated', this.onCartUpdated);
      }

      /** @returns {string[]} Discount codes already applied to the cart. */
      _appliedDiscountCodes() {
        const signature = window.__cartStateSignature;

        if (!signature) return [];

        try {
          const cart = JSON.parse(signature);
          const applications = cart.cart_level_discount_applications;

          if (!Array.isArray(applications)) return [];

          return applications
            .filter((application) => application.type === 'discount_code' && application.title)
            .map((application) => String(application.title));
        } catch {
          return [];
        }
      }

      /**
       * @param {string} code - Discount code to add.
       * @returns {string[]} Full discount code list for the cart.
       */
      _discountCodesWithApplied(code) {
        const appliedCodes = this._appliedDiscountCodes();

        if (appliedCodes.some((appliedCode) => appliedCode.toLowerCase() === code.toLowerCase())) {
          return appliedCodes;
        }

        return [...appliedCodes, code];
      }

      /** @returns {string[]} Unused reward codes not yet on the cart, highest value first. */
      _unusedRewardCodes() {
        const appliedCodes = this._appliedDiscountCodes();
        const appliedLower = new Set(appliedCodes.map((code) => code.toLowerCase()));

        return (window.rewardsData?.unused_rewards ?? [])
          .filter((reward) => !appliedLower.has(reward.code.toLowerCase()))
          .sort((a, b) => Number.parseFloat(b.amount) - Number.parseFloat(a.amount))
          .map((reward) => reward.code);
      }

      /** @returns {string[]} Applied cart codes plus up to 5 unused reward codes. */
      _discountCodesWithAllApplied() {
        const appliedCodes = this._appliedDiscountCodes();
        const slotsRemaining = 5 - appliedCodes.length;

        if (slotsRemaining <= 0) return appliedCodes;

        return [...appliedCodes, ...this._unusedRewardCodes().slice(0, slotsRemaining)];
      }

      /**
       * @param {MouseEvent} e - Apply button click.
       */
      async handleApplyClick(e) {
        e.preventDefault();

        const target = e.target;

        if (!(target instanceof Element)) return;

        const applyAllElement = target.closest('[data-rewards-unused-apply-all]');
        const applyCodeElement = target.closest('[data-rewards-apply-code]');
        const element = applyAllElement ?? applyCodeElement;

        if (!(element instanceof HTMLElement)) return;

        if (this.dataset.loading === 'true') return;

        const isApplyAll = applyAllElement instanceof HTMLElement;
        const code = element.dataset.rewardsApplyCode;

        if (!isApplyAll && !code) return;

        const cartForm = document.querySelector('cart-form');

        if (!cartForm) return;

        this.dataset.loading = 'true';

        let discountCodes;

        if (isApplyAll) {
          discountCodes = this._discountCodesWithAllApplied();
        } else if (code) {
          discountCodes = this._discountCodesWithApplied(code);
        } else {
          this.dataset.loading = 'false';

          return;
        }

        // Shopify only allows 5 discount codes to be applied to a cart.
        if (discountCodes.length > 5) {
          this.dataset.loading = 'false';

          return;
        }

        try {
          await cartForm.updateDiscounts(discountCodes);
        } catch {
          // Discount apply failed; loading state reset in finally.
        } finally {
          this.dataset.loading = 'false';
          document.querySelector('rewards-context')?.refresh();
        }
      }

      /**
       * @param {typeof window.rewardsData} data - Rewards payload.
       */
      render(data) {
        const appliedCodes = this._appliedDiscountCodes();
        const rewardCodes = data?.unused_rewards?.map((reward) => reward.code.toLowerCase()) || [];
        const rewardCodeSet = new Set(rewardCodes);
        const nonRewardCartCodes = appliedCodes.filter((code) => !rewardCodeSet.has(code.toLowerCase()));
        const combinedCodes = Array.from(new Set([...rewardCodes, ...nonRewardCartCodes]));

        if (this.applyAllButton instanceof HTMLButtonElement) {
          this.applyAllButton.classList.toggle('hidden', appliedCodes.length >= 5 || combinedCodes.length > 5);
        }
      }
    },
  );
}

if (!customElements.get('rewards-active-rewards')) {
  customElements.define(
    'rewards-active-rewards',
    class extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          list: this.querySelector('[data-rewards-active-rewards-list]'),
          total: this.querySelector('[data-rewards-active-rewards-total]'),
        };

        this.onRewardsDataUpdated = (/** @type {Event} */ event) =>
          this.render(/** @type {CustomEvent<typeof window.rewardsData>} */ (event).detail);

        this.onCartUpdated = () => this.render(window.rewardsData);
        this.handleRemoveClick = this.handleRemoveClick.bind(this);
      }

      connectedCallback() {
        this.render(window.rewardsData);
        window.addEventListener('rewards-data-updated', this.onRewardsDataUpdated);
        window.addEventListener('cart-updated', this.onCartUpdated);
        this.addEventListener('click', this.handleRemoveClick);
      }

      disconnectedCallback() {
        window.removeEventListener('rewards-data-updated', this.onRewardsDataUpdated);
        window.removeEventListener('cart-updated', this.onCartUpdated);
        this.removeEventListener('click', this.handleRemoveClick);
      }

      /**
       * @param {string} value - The value to format.
       * @param {number} multiplier - The multiplier to apply to the value.
       * @returns {string} The formatted value.
       */
      _money(value, multiplier) {
        const amount = Number.parseFloat(value) * multiplier;
        /** @type {HTMLElement | null} */
        const panel = this.closest('rewards-panel');

        return new Intl.NumberFormat(document.documentElement.lang, {
          style: 'currency',
          currency: panel?.dataset.currency || window.Shopify.currency.active,
        }).format(amount);
      }

      /** @returns {string[]} Discount codes already applied to the cart. */
      _appliedDiscountCodes() {
        const signature = window.__cartStateSignature;

        if (!signature) return [];

        try {
          const cart = JSON.parse(signature);
          const applications = cart.cart_level_discount_applications;

          if (!Array.isArray(applications)) return [];

          return applications
            .filter((application) => application.type === 'discount_code' && application.title)
            .map((application) => String(application.title));
        } catch {
          return [];
        }
      }

      /**
       * @param {typeof window.rewardsData} data - Rewards payload.
       * @returns {NonNullable<NonNullable<typeof window.rewardsData>['unused_rewards']>} Applied rewards from the rewards context.
       */
      _activeRewards(data) {
        if (!data?.unused_rewards?.length) return [];

        const rewardsByCode = new Map(data.unused_rewards.map((reward) => [reward.code.toLowerCase(), reward]));

        return this._appliedDiscountCodes()
          .map((code) => rewardsByCode.get(code.toLowerCase()))
          .filter((reward) => reward !== undefined);
      }

      /**
       * @param {string} code - Discount code to remove.
       * @returns {string[]} Remaining discount codes for the cart.
       */
      _discountCodesWithout(code) {
        return this._appliedDiscountCodes().filter((appliedCode) => appliedCode.toLowerCase() !== code.toLowerCase());
      }

      /**
       * @param {MouseEvent} e - Remove button click.
       */
      async handleRemoveClick(e) {
        e.preventDefault();

        const target = e.target;

        if (!(target instanceof Element)) return;

        const element = target.closest('[data-rewards-remove-code]');

        if (!(element instanceof HTMLElement)) return;

        if (this.dataset.loading === 'true') return;

        const code = element.dataset.rewardsRemoveCode;

        if (!code) return;

        const cartForm = document.querySelector('cart-form');

        if (!cartForm) return;

        this.dataset.loading = 'true';

        const discountCodes = this._discountCodesWithout(code);

        try {
          await cartForm.updateDiscounts(discountCodes);
        } catch {
          // Discount remove failed; loading state reset in finally.
        } finally {
          this.dataset.loading = 'false';
          document.querySelector('rewards-context')?.refresh();
        }
      }

      /**
       * @param {typeof window.rewardsData} data - Rewards payload.
       */
      async render(data) {
        if (!this.elements.list || !this.elements.total) return;

        const activeRewards = this._activeRewards(data);

        this.hidden = activeRewards.length === 0;
        this.elements.list.replaceChildren();

        if (activeRewards.length === 0) {
          this.elements.total.textContent = '';

          return;
        }

        const multiplier = await window.getExchangeRateMultiplier();

        for (const reward of activeRewards) {
          const element = document.createElement('li');
          element.classList.add('rewards-panel-active-reward');
          element.innerHTML = `
            <span class="text-discount-tag rewards-panel-reward-tag">${this.dataset.tagIconMarkup || ''} ${reward.code} <button class="icon-button text-button--small text-button--no-underline" type="button" data-rewards-remove-code="${reward.code}">${this.dataset.crossIconMarkup || ''} <span class="visually-hidden">${this.dataset.translationRemove || 'Remove'}</span></button></span>
          `;
          this.elements.list.append(element);
        }

        const total = String(activeRewards.reduce((acc, reward) => acc + Number.parseFloat(reward.amount), 0));
        this.elements.total.textContent = `-${this._money(total, multiplier)}`;
      }
    },
  );
}
